import React from "react";
import { Link } from "react-router-dom";
import { Lock, ArrowLeft } from "lucide-react";
import { usePermissions } from "@/lib/usePermissions";

/**
 * Forbidden — full-page state for authenticated users who lack a permission.
 *
 * This is NOT a redirect to /login: the session and tenant context are
 * preserved. Use this as a route-level guard fallback, or wherever a hard
 * "access denied" message is more appropriate than hiding/disabling controls.
 *
 * Props:
 *   - title:      override main title (default Italian copy)
 *   - message:    override the explanatory paragraph
 *   - ctaTo:      where the "back" button leads (default /hotel/today)
 *   - ctaLabel:   button label
 *   - missingPermission: optional permission id surfaced as debug hint
 */
export default function Forbidden({
  title = "Accesso negato",
  message = "Non hai i permessi per visualizzare questa sezione.",
  ctaTo = "/hotel/today",
  ctaLabel = "Torna al Centro Operativo",
  missingPermission,
}) {
  const { roleLabel, role } = usePermissions();

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="forbidden-state">
      <div className="surface-card max-w-md w-full p-8 text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-[#FEF2F2] grid place-items-center mb-4">
          <Lock className="w-7 h-7 text-[#B91C1C]" />
        </div>
        <h1 className="font-heading text-2xl font-bold text-[#0f172a]" data-testid="forbidden-title">
          {title}
        </h1>
        <p className="text-sm text-[#475569] mt-2" data-testid="forbidden-message">
          {message}
        </p>

        <div className="mt-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#94A3B8] bg-[#F1F5F9] px-2 py-1 rounded-full" data-testid="forbidden-role">
          {(roleLabel || role || "—").toString()}
        </div>

        {missingPermission && (
          <p className="mt-2 text-[11px] text-[#94A3B8] font-mono" data-testid="forbidden-permission">
            Permesso richiesto: {missingPermission}
          </p>
        )}

        <div className="mt-6 flex items-center justify-center gap-2">
          <Link
            to={ctaTo}
            className="h-10 px-4 inline-flex items-center gap-1.5 text-sm font-semibold rounded-md bg-[#102A43] text-white hover:bg-[#0b1b2c]"
            data-testid="forbidden-cta"
          >
            <ArrowLeft className="w-4 h-4" /> {ctaLabel}
          </Link>
        </div>

        <p className="mt-4 text-[11px] text-[#94A3B8]">
          Se ritieni di dover accedere a questa funzione, contatta il titolare o il direttore della struttura.
        </p>
      </div>
    </div>
  );
}
